import React, { FC } from "react";
import { Collapse } from "antd";
import { Phase, Task, Work, PricingType, TeamMembershipIds } from "../../data-types";
import { TaskList } from "../tasks/task-list";
import { FeeEarnerList } from "../fee-earners";
import { useTaxonomyLabel } from "~/context/taxonomy-context";
import { TeamMembershipPartsFragment } from "~/gql/graphql";

type Props = {
  matterId: number;
  template?: boolean;
  proposalTitle?: string;
  phase: Phase;
  onUpdate: (phase: Phase) => void;
  onDelete: (id: number) => void;
  onUpdateTask: (task: Partial<Task>) => void;
  onDeleteTask: (id: number) => void;
  onCreateTask: () => void;
  onCreateTasks: (taskNames: string[]) => void;
  onUpdateFeeEarner: (work: Work, timeEntryUpdate: boolean) => void;
  onDeleteFeeEarner: (id: number) => void;
  onCreateFeeEarner: () => void;
  onCopyFromPreviousPhase: () => void;
  showFeeEarners?: boolean;
  onReorderTasks: (activeId: number, overId: number) => void;
  pricingType: PricingType;
  teamMembers: TeamMembershipPartsFragment[];
  currencySymbol: string;
  useAi?: boolean;
  autoGenerated?: boolean;
  countryCode?: string;
  onTimelineEdit: (context: {
    id: number;
    title: string;
    startDate?: Date | null;
    deadlineDate?: Date | null;
  }) => void;
};

export const PhaseContent: FC<Props> = ({
  matterId,
  template,
  proposalTitle,
  phase: { tasks, work, id: phaseId },
  onUpdateTask,
  onCreateTask,
  onCreateTasks,
  onDeleteTask,
  onDeleteFeeEarner,
  onUpdateFeeEarner,
  onCreateFeeEarner,
  onCopyFromPreviousPhase,
  showFeeEarners,
  onReorderTasks,
  pricingType,
  teamMembers,
  currencySymbol,
  useAi,
  autoGenerated,
  countryCode,
  onTimelineEdit,
}) => {
  const includedTeamMemberships: TeamMembershipIds[] =
    work
      ?.filter((w) => w.selectedRate)
      .flatMap((w) => {
        return {
          rateId: w.selectedRate!.id,
          feeEarnerId: w.feeEarner?.id ?? null,
        };
      }) || [];

  const taxonomy = useTaxonomyLabel();

  return (
    <>
      {!template && showFeeEarners && (
        <>
          <Collapse ghost defaultActiveKey={"1"}>
            <Collapse.Panel header={`${taxonomy.FeeEarner}s`} key="1" forceRender>
              <FeeEarnerList
                currencySymbol={currencySymbol}
                work={work}
                onDelete={onDeleteFeeEarner}
                onCreate={onCreateFeeEarner}
                onUpdate={onUpdateFeeEarner}
                onCopyFromPreviousPhase={onCopyFromPreviousPhase}
                range={pricingType === PricingType.EstimatedRange}
                teamMembers={teamMembers}
                includedTeamMemberships={includedTeamMemberships}
                matterId={matterId}
              />
            </Collapse.Panel>
          </Collapse>
        </>
      )}
      <Collapse ghost defaultActiveKey={"1"}>
        <Collapse.Panel header="Tasks" key="1" forceRender>
          <TaskList
            matterId={matterId}
            proposalTitle={proposalTitle}
            phaseId={phaseId}
            onReorder={onReorderTasks}
            tasks={tasks}
            onUpdate={onUpdateTask}
            onCreate={onCreateTask}
            onDelete={onDeleteTask}
            onCreateTasks={onCreateTasks}
            useAi={useAi}
            autoGenerated={autoGenerated}
            countryCode={countryCode}
            onTimelineEdit={onTimelineEdit}
          />
        </Collapse.Panel>
      </Collapse>
    </>
  );
};
